import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

export const ProjectStructure: React.FC = () => {
  const [structure, setStructure] = useState<string>('');
  const [format, setFormat] = useState<'full' | 'compact'>('full');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStructure();
  }, [format]);

  const fetchStructure = async () => {
    setLoading(true);
    try {
      const response = await fetch(`http://localhost:8787/api/structure?format=${format === 'full' ? 'text' : 'compact'}`);
      const text = await response.text();
      setStructure(text);
    } catch (error) {
      setStructure('Error loading project structure. Make sure the API is running on port 8787.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-green-400 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Project Structure
          </h1>
          <p className="text-gray-400 mb-6">
            Complete ASCII map of the CloudFlare Full-Stack Boilerplate
          </p>
          
          <div className="flex justify-center gap-4 mb-6">
            <button
              onClick={() => setFormat('full')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                format === 'full'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Full View
            </button>
            <button
              onClick={() => setFormat('compact')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                format === 'compact'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Compact View
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-black rounded-lg p-4 md:p-6 overflow-x-auto border border-gray-800"
        >
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
              <p className="mt-4 text-gray-400">Loading structure...</p>
            </div>
          ) : (
            <pre className="font-mono text-xs md:text-sm whitespace-pre overflow-x-auto">
              {structure}
            </pre>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-xl font-bold mb-3 text-blue-400">🌐 Public Access</h3>
            <p className="text-gray-300 text-sm mb-3">
              This structure is exposed via API for documentation tools and AI assistants.
            </p>
            <code className="bg-gray-900 px-3 py-1 rounded text-xs block">
              GET /api/structure
            </code>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-xl font-bold mb-3 text-green-400">📡 API Formats</h3>
            <ul className="text-gray-300 text-sm space-y-2">
              <li>• JSON (default)</li>
              <li>• Plain text (?format=text)</li>
              <li>• Compact (?format=compact)</li>
            </ul>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-xl font-bold mb-3 text-purple-400">🤖 AI-Friendly</h3>
            <p className="text-gray-300 text-sm">
              ASCII format ensures compatibility with all AI models and terminal-based tools.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 bg-gray-800 rounded-lg p-6 border border-gray-700"
        >
          <h3 className="text-xl font-bold mb-4 text-yellow-400">🔗 Direct Links</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href="http://localhost:8787/api/structure"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
            >
              <span>📄</span>
              <span>JSON Format</span>
              <span className="text-gray-500">→</span>
            </a>
            <a
              href="http://localhost:8787/api/structure?format=text"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
            >
              <span>📝</span>
              <span>Plain Text Format</span>
              <span className="text-gray-500">→</span>
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};